import austinImage from 'assets/austin.jpg';
import headerImage from 'assets/header.jpg';
import React from 'react';

import { renderCityCards } from 'utilities/renderCityCard';

interface City {
  city: string;
  state: string;
  inHouse: number;
  upcoming: number;
}

interface DynamicCityGridProps {
  cities: City[];
}

// Type definitions for layout configuration
type LayoutItem =
  | { type: 'city'; index: number; className: string }
  | { type: 'image'; src: string; alt: string; className: string };

type GridLayouts = Record<1 | 2 | 3 | 4 | 5 | 6, LayoutItem[]>;

// Reusable image card component
const ImageCard = React.memo(
  ({
    src,
    alt,
    className = '',
  }: {
    src: string;
    alt: string;
    className?: string;
  }) => (
    <div
      className={`rounded-lg bg-cover bg-center bg-no-repeat ${className}`}
      style={{ backgroundImage: `url(${src})` }}
      role="img"
      aria-label={alt}
    />
  ),
);

ImageCard.displayName = 'ImageCard';

const MAX_SUPPORTED_CITIES = 6;

// Grid layout configurations for different city counts
const GRID_LAYOUTS: GridLayouts = {
  1: [
    {
      type: 'city',
      index: 0,
      className: 'col-span-1 lg:col-span-2 min-h-[300px]',
    },
    {
      type: 'image',
      src: headerImage,
      alt: 'Travel header image',
      className: 'col-span-1 lg:col-span-2 min-h-[300px]',
    },
  ],
  2: [
    {
      type: 'city',
      index: 0,
      className: 'col-span-1 lg:col-span-1 min-h-[300px]',
    },
    {
      type: 'city',
      index: 1,
      className: 'col-span-1 lg:col-span-1 min-h-[300px]',
    },
    {
      type: 'image',
      src: headerImage,
      alt: 'Travel header image',
      className: 'col-span-1 lg:col-span-2 min-h-[300px]',
    },
  ],
  3: [
    {
      type: 'city',
      index: 0,
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
    {
      type: 'city',
      index: 1,
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
    {
      type: 'city',
      index: 2,
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
    {
      type: 'image',
      src: headerImage,
      alt: 'Travel header image',
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
  ],
  4: [
    {
      type: 'city',
      index: 0,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'city',
      index: 1,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'image',
      src: headerImage,
      alt: 'Travel header image',
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
    {
      type: 'image',
      src: austinImage,
      alt: 'Austin city skyline',
      className:
        'col-span-1 lg:col-span-2 min-h-[200px] order-last lg:order-none',
    },
    {
      type: 'city',
      index: 2,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'city',
      index: 3,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
  ],
  5: [
    {
      type: 'city',
      index: 0,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'city',
      index: 1,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'image',
      src: headerImage,
      alt: 'Travel header image',
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
    {
      type: 'image',
      src: austinImage,
      alt: 'Austin city skyline',
      className:
        'col-span-1 lg:col-span-2 min-h-[200px] order-last lg:order-none',
    },
    {
      type: 'city',
      index: 2,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'city',
      index: 3,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'city',
      index: 4,
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
  ],
  6: [
    {
      type: 'city',
      index: 0,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'city',
      index: 1,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'image',
      src: headerImage,
      alt: 'Travel header image',
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
    {
      type: 'image',
      src: austinImage,
      alt: 'Austin city skyline',
      className:
        'col-span-1 lg:col-span-2 min-h-[200px] order-last lg:order-none',
    },
    {
      type: 'city',
      index: 2,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'city',
      index: 3,
      className: 'col-span-1 lg:col-span-1 min-h-[200px]',
    },
    {
      type: 'city',
      index: 4,
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
    {
      type: 'city',
      index: 5,
      className: 'col-span-1 lg:col-span-2 min-h-[200px]',
    },
  ],
};

export default function DynamicCityGrid({
  cities,
}: DynamicCityGridProps): JSX.Element | null {
  if (!cities || cities.length === 0) return null;

  // Get the appropriate layout configuration
  const cityCount = Math.min(
    cities.length,
    MAX_SUPPORTED_CITIES,
  ) as keyof GridLayouts;
  const layout = GRID_LAYOUTS[cityCount];

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
      {layout.map((item, idx) => {
        if (item.type === 'city') {
          return (
            <React.Fragment key={`city-${item.index}`}>
              {renderCityCards(
                cities,
                item.index,
                item.index + 1,
                item.className,
              )}
            </React.Fragment>
          );
        } else {
          return (
            <ImageCard
              key={`image-${idx}`}
              src={item.src}
              alt={item.alt}
              className={item.className}
            />
          );
        }
      })}
    </div>
  );
}
